import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Box, Avatar, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Divider, Menu, MenuItem, ListItemIcon, ListItemText, Select, FormControl,
  InputLabel, useTheme,
} from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import { useApp } from '../context/AppContext';
import { useThemeMode } from '../main';

export default function TopNav() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { toggleTheme, mode: themeMode } = useThemeMode();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const { user, projects, currentProject, logout, createProject, selectProject, loadProjects } = useApp();

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [nameError, setNameError] = useState('');
  const [creating, setCreating] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const borderColor = isDark ? '#2C2C2E' : '#E5E5EA';
  const surfaceBg = isDark ? '#121212' : '#F4F4F8';

  // suppress unused variable warning
  void projectId;

  const handleProjectChange = (value: string) => {
    if (value === '__create__') { setCreateOpen(true); return; }
    selectProject(value);
    navigate(`/projects/${value}/flags`);
  };

  const handleCreate = async () => {
    setNameError('');
    if (!newName.trim()) { setNameError('Project name is required.'); return; }
    setCreating(true);
    try {
      const project = await createProject(newName.trim());
      await loadProjects();
      setCreateOpen(false);
      setNewName('');
      navigate(`/projects/${project.projectId}/flags`);
    } catch { setNameError('Failed to create project.'); }
    finally { setCreating(false); }
  };

  const initials = user?.email ? user.email.slice(0, 2).toUpperCase() : '?';

  return (
    <>
      <AppBar position="sticky" elevation={0} sx={{ borderBottom: `1px solid ${borderColor}`, bgcolor: 'background.paper', zIndex: 1200 }}>
        <Toolbar sx={{ justifyContent: 'space-between', py: 1, minHeight: 70 }}>
          {/* LEFT: Logo + Project Selector */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 4 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={() => navigate('/')}>
              <Box sx={{ bgcolor: 'primary.main', p: 0.75, borderRadius: 2, display: 'flex' }}>
                <FlagIcon sx={{ color: '#fff', fontSize: 22 }} />
              </Box>
              <Typography sx={{ fontWeight: 800, letterSpacing: '0.06em', color: 'text.primary', fontSize: 18, display: { xs: 'none', sm: 'block' } }}>
                HAKI
              </Typography>
            </Box>

            <FormControl variant="outlined" size="small" sx={{ minWidth: { xs: 160, sm: 220 } }}>
              <InputLabel id="project-select-label">Project</InputLabel>
              <Select
                labelId="project-select-label"
                value={currentProject?.projectId || (projects[0]?.projectId ?? '')}
                onChange={e => handleProjectChange(e.target.value)}
                label="Project"
                sx={{ bgcolor: surfaceBg, borderRadius: 2 }}
                renderValue={val => projects.find(p => p.projectId === val)?.name ?? 'Select project'}
              >
                {projects.map(p => (
                  <MenuItem key={p.projectId} value={p.projectId}>{p.name}</MenuItem>
                ))}
                <Divider sx={{ my: 0.5 }} />
                <MenuItem value="__create__" sx={{ color: 'primary.main', fontWeight: 700, gap: 1, '&:hover': { bgcolor: 'rgba(255,59,48,0.06)' } }}>
                  <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
                  Create New Project
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* RIGHT: Theme toggle + User avatar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            <Tooltip title={themeMode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
              <IconButton onClick={toggleTheme} size="small" sx={{ border: `1px solid ${borderColor}`, bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }, width: 36, height: 36 }}>
                {themeMode === 'dark' ? <LightModeIcon sx={{ fontSize: 17, color: 'text.secondary' }} /> : <DarkModeIcon sx={{ fontSize: 17, color: 'text.secondary' }} />}
              </IconButton>
            </Tooltip>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={e => setAnchorEl(e.currentTarget)}>
              <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                <Typography sx={{ fontWeight: 600, color: 'text.primary', fontSize: 14 }}>{user?.email?.split('@')[0] ?? 'User'}</Typography>
                <Typography sx={{ color: 'secondary.main', fontWeight: 500, fontSize: 12 }}>Admin</Typography>
              </Box>
              <Avatar sx={{ bgcolor: isDark ? '#3A3A3C' : '#E5E5EA', color: isDark ? '#F5F5F7' : '#1C1C1E', fontWeight: 700, fontSize: 14, border: `2px solid ${borderColor}`, width: 38, height: 38 }}>
                {initials}
              </Avatar>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* User menu */}
      <Menu
        anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{ sx: { mt: 1, minWidth: 200, border: `1px solid ${borderColor}`, boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.12)', borderRadius: 2 } }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${borderColor}` }}>
          <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: 14 }}>{user?.email?.split('@')[0]}</Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>{user?.email}</Typography>
        </Box>
        <MenuItem onClick={() => setAnchorEl(null)} sx={{ mt: 0.5, gap: 1.5, py: 1.2 }}>
          <ListItemIcon><PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></ListItemIcon>
          <ListItemText primary="Profile" primaryTypographyProps={{ fontSize: 14 }} />
        </MenuItem>
        <MenuItem onClick={() => { setAnchorEl(null); logout(); navigate('/login'); }} sx={{ gap: 1.5, py: 1.2, color: 'primary.main' }}>
          <ListItemIcon><LogoutIcon sx={{ fontSize: 18, color: 'primary.main' }} /></ListItemIcon>
          <ListItemText primary="Sign out" primaryTypographyProps={{ fontSize: 14, color: 'primary.main', fontWeight: 600 }} />
        </MenuItem>
      </Menu>

      {/* Create Project Dialog */}
      <Dialog open={createOpen} onClose={() => { setCreateOpen(false); setNewName(''); setNameError(''); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, border: `1px solid ${borderColor}` } }}>
        <DialogTitle sx={{ fontWeight: 800, color: 'text.primary', pb: 1, fontSize: 18 }}>Create New Project</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Typography sx={{ color: 'text.secondary', fontSize: 14, mb: 2.5, lineHeight: 1.5 }}>
            Projects group your feature flags. Give your project a clear, descriptive name.
          </Typography>
          <TextField
            label="Project Name" value={newName}
            onChange={e => { setNewName(e.target.value); setNameError(''); }}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
            fullWidth autoFocus placeholder="e.g. Mobile App v2"
            error={!!nameError} helperText={nameError}
            InputProps={{ sx: { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => { setCreateOpen(false); setNewName(''); setNameError(''); }} sx={{ color: 'text.secondary', fontWeight: 600, borderRadius: 2, px: 2.5 }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating} sx={{ borderRadius: 2, px: 3, fontWeight: 700 }}>Create Project</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
